import React from 'react';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../utils/useTranslation';
import { motion } from 'framer-motion';
import logo from '../../assets/logo.jpeg';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);

  // Role-based logic
  const role = user?.role || '';
  const isMain = role === 'main_admin';
  const isCenterBody = role.startsWith('center_body_');
  const isDistCoord = role === 'district_coordinator';
  const isBranchSecretary = role === 'branch_admin' || role === 'branch_secretary_general';
  const isMember = role === 'member' || role === 'branch_auditor' || role === 'branch_treasurer' || role === 'branch_joint_secretary' || role === 'branch_counselor';

  const isEffectiveAdmin = isMain || isCenterBody;
  const isAdminView = isEffectiveAdmin || isBranchSecretary || isDistCoord;
  const canManageStructure = isEffectiveAdmin || isDistCoord;
  const isOfficer = !isMember;

  const isRTL = t.language === 'sindhi';
  const sidebarWidth = collapsed ? 88 : 280;

  const NavItem = ({ to, icon, label, end }) => (
    <NavLink 
      to={to} 
      end={end}
      className={({ isActive }) => isActive ? 'active' : ''}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 16,
        padding: collapsed ? '12px 0' : '12px 20px',
        margin: collapsed ? '4px 12px' : '4px 16px',
        borderRadius: 12,
        textDecoration: 'none',
        color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
        background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
        fontWeight: isActive ? 700 : 500,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: (isActive && !collapsed) ? (isRTL ? 'inset -4px 0 0 var(--sindhi-gold)' : 'inset 4px 0 0 var(--sindhi-gold)') : 'none',
        position: 'relative'
      })}
    >
      {({ isActive }) => (
        <>
          <span style={{ fontSize: 20, width: 24, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
          {!collapsed && <span style={{ fontSize: 14, letterSpacing: 0.3, whiteSpace: 'nowrap', opacity: 1, transition: 'opacity 0.2s' }}>{label}</span>}
          {collapsed && isActive && (
            <div style={{ position: 'absolute', bottom: 4, width: 4, height: 4, borderRadius: '50%', background: 'var(--sindhi-gold)' }} />
          )}
        </>
      )}
    </NavLink>
  );

  const SectionLabel = ({ label }) => (
    collapsed ? <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '16px 20px' }} /> :
      <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1.5, margin: '24px 24px 12px 24px' }}>{label}</div>
  );

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'var(--sindhi-ivory)',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      {/* ── SIDEBAR ── */}
      <aside style={{
        width: sidebarWidth,
        background: 'linear-gradient(180deg, var(--sindhi-navy-mid), var(--sindhi-navy))',
        borderRight: (isRTL || collapsed) ? 'none' : '1px solid rgba(255,255,255,0.05)',
        borderLeft: (!isRTL || collapsed) ? 'none' : '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: isRTL ? 'auto' : 0,
        right: isRTL ? 0 : 'auto',
        zIndex: 100,
        boxShadow: isRTL ? '-4px 0 24px rgba(10,25,47,0.2)' : '4px 0 24px rgba(10,25,47,0.2)',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {/* Pattern overlay */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4v-4H4v4H0v2h4v4h2v-4h4v-2H6zm30 0v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }} />

        {/* Toggle Button */}
        <motion.button
          whileHover={{ scale: 1.1, backgroundColor: 'var(--sindhi-ivory)' }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setCollapsed(!collapsed)}
          style={{
            position: 'absolute',
            right: isRTL ? 'auto' : -12,
            left: isRTL ? -12 : 'auto',
            top: 32,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'white',
            border: '1px solid var(--sindhi-cream)',
            color: 'var(--sindhi-navy)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
            fontSize: 14,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {isRTL ? (
            collapsed ? <FiChevronLeft /> : <FiChevronRight />
          ) : (
            collapsed ? <FiChevronRight /> : <FiChevronLeft />
          )}
        </motion.button>

        <div style={{ padding: collapsed ? '32px 0' : '32px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 16 }}>
            <img src={logo} alt="Logo" style={{
              width: collapsed ? 40 : 50,
              height: collapsed ? 40 : 50,
              borderRadius: 12,
              objectFit: 'cover',
              border: '2px solid var(--sindhi-gold)',
              transition: 'all 0.3s'
            }} />
            {!collapsed && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--sindhi-gold)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                  {isAdminView ? t('adminPanel') : t('memberPanel')}
                </div>
              </div>
            )}
          </Link>
        </div>

        {/* User info */}
        {!collapsed && (
          <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.05)', position: 'relative' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>{user?.displayName || user?.name}</div>
            <div style={{
              fontSize: 11, color: 'var(--sindhi-gold)', marginTop: 6, textTransform: 'uppercase',
              letterSpacing: 1, fontWeight: 800, background: 'rgba(255,255,255,0.1)',
              padding: '4px 10px', borderRadius: 8, display: 'inline-block'
            }}>
              {t(role) || role?.replace(/_/g, ' ')}
            </div>
          </div>
        )}

        {/* Nav — Role-Based */}
        <nav style={{ padding: '24px 0', overflowY: 'auto', flex: 1, position: 'relative' }}>
          <SectionLabel label={t('overview')} />
          <NavItem to="/admin" end icon="📊" label={t('dashboard')} />
          {isMember && <NavItem to="/admin/my-writings" icon="✍️" label={t('myWritings')} />}

          {/* Requests & Structure */}
          <SectionLabel label={t('requestsApprovals')} />
          {(isDistCoord || isCenterBody) && <NavItem to="/admin/transfers" icon="🔄" label={t('branchTransferRequests')} />}
          {canManageStructure && <NavItem to="/admin/branches" icon="🌿" label={t('manageBranches')} />}
          {isEffectiveAdmin && <NavItem to="/admin/districts" icon="🗺️" label={t('districts')} />}
          <NavItem to="/admin/members" icon="👥" label={isBranchSecretary ? t('manageMembers') : (isRTL ? t('members') : 'Members')} />
          {isEffectiveAdmin && <NavItem to="/admin/users" icon="👤" label={isMain ? t('manageDistrictCouncilors') : t('manageUsers')} />}

          {/* Content Management */}
          <SectionLabel label={t('contentManagement')} />
          <NavItem to="/admin/books" icon="📚" label={isAdminView ? t('manageBooks') : (isRTL ? t('members') : 'Books')} />
          <NavItem to="/admin/board" icon="🏛️" label={isEffectiveAdmin ? t('manageBoardMembers') : (isRTL ? t('board') : 'Board')} />
          {isEffectiveAdmin && <NavItem to="/admin/literary-fields" icon="✍️" label={t('literaryFields')} />}
          <NavItem to="/admin/events" icon="📅" label={isAdminView ? t('manageEvents') : (isRTL ? t('events') : 'Events')} />
          {isEffectiveAdmin && <NavItem to="/admin/votes" icon="🗳️" label={t('voting')} />}

          {/* System — main_admin only */}
          {isMain && <>
            <SectionLabel label={isRTL ? 'سسٽم' : 'System'} />
            <NavItem to="/admin/migrate" icon="🚀" label={t('dataMigration')} />
            <NavItem to="/admin/seed-data" icon="🌱" label={isRTL ? 'سيڊ ڊيٽا' : 'Seed Data'} />
          </>}
        </nav>

        {/* Bottom */}
        <div style={{ marginTop: 'auto', padding: '24px 16px', borderTop: '1px solid rgba(255,255,255,0.05)', position: 'relative' }}>
          <Link
            to="/"
            style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 12, padding: '12px 20px', borderRadius: 12, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontWeight: 600, transition: 'all 0.2s' }}
            title={collapsed ? t('viewWebsite') : ''}
          >
            <span style={{ fontSize: 20 }}>🌐</span>{!collapsed && <span>{t('viewWebsite')}</span>}
          </Link>
          <button
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 12, padding: '12px 20px', borderRadius: 12, color: '#fca5a5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textAlign: 'left', transition: 'all 0.2s', marginTop: 4 }}
            onClick={() => { logout(); navigate('/login'); }}
            title={collapsed ? t('logout') : ''}
          >
            <span style={{ fontSize: 20 }}>🚪</span>{!collapsed && <span>{t('logout')}</span>}
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main style={{
        flex: 1,
        marginLeft: isRTL ? 0 : sidebarWidth,
        marginRight: isRTL ? sidebarWidth : 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: `calc(100% - ${sidebarWidth}px)`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {/* Top bar */}
        <header style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 50,
          boxShadow: '0 4px 30px -10px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, color: 'var(--sindhi-navy)', letterSpacing: -0.5 }}>
            {isAdminView ? t('adminTitle') : t('memberTitle')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {user?.branch && !collapsed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'white', padding: '8px 16px', borderRadius: 100, border: '1px solid var(--sindhi-cream)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <span style={{ fontSize: 16 }}>📍</span>
                <span style={{ fontSize: 13, color: 'var(--sindhi-navy)', fontWeight: 700 }}>
                  {typeof user.branch === 'object' ? user.branch.name : 'Branch Admin'}
                </span>
              </div>
            )}
            <motion.div
              whileHover={{ scale: 1.05 }}
              style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'linear-gradient(135deg, var(--sindhi-maroon), var(--sindhi-navy))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 800, color: 'white',
                boxShadow: '0 8px 16px -4px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              {(user?.displayName || user?.name || '?').charAt(0)}
            </motion.div>
          </div>
        </header>

        <div style={{ padding: '40px', flex: 1, maxWidth: 1400, margin: '0 auto', width: '100%' }}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </div>

      </main>
    </div>
  );
}
